import { useMemo, useState } from "react";
import {
  Download, ExternalLink, FileText, Paperclip, Pencil, Search, Trash2, Upload,
} from "lucide-react";
import { money, calcCommission, effectiveRate, calcAgentCommission } from "../lib/finance";
import { PageTitle, Empty } from "./common";
import { CompanyName } from "../lib/companies";

export function PolicyTable({ policies, customers, branches, settings, compact = false, remove, onEdit, openPolicyPdf, canDelete = true }) {
  const anyAgent = !compact && policies.some((p) => customers && customerCommissionRateInline(p, customers) > 0);
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>POLİÇE NO</th><th>MÜŞTERİ</th><th>BRANŞ</th><th>ŞİRKET</th><th>PLAKA</th><th>BRÜT PRİM</th><th>HAK EDİŞ</th>{anyAgent && <th>ACENTE PRİMİ</th>}{!compact && <th>İŞLEM</th>}
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => {
            const c = calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null);
            const ag = customers ? calcAgentCommission(p, customers, branches, settings) : { agent: 0, rate: 0 };
            return (
              <tr key={p.id} data-testid={`policy-row-${p.id}`}>
                <td>
                  <strong>{p.policeNo}</strong>
                  <small>{p.start} — {p.end}</small>
                  {(p.status || "aktif") === "iptal" && <span data-testid={`policy-cancelled-${p.id}`} title={`İptal · İade: ${p.cancellationRefund != null && p.cancellationRefund !== "" ? money(p.cancellationRefund) : "yok"}`} style={{ marginLeft: 6, fontSize: 9, padding: "2px 5px", borderRadius: 3, background: "#fee2e2", color: "#991b1b", fontWeight: 700, letterSpacing: 0.3, verticalAlign: "middle" }}>İPTAL</span>}
                  {(() => {
                    const count = Array.isArray(p.pdfs) && p.pdfs.length > 0 ? p.pdfs.length : (p.pdfPath ? 1 : 0);
                    if (count === 0) return null;
                    return (
                      <>
                        <span className="pdf-badge" title={count > 1 ? `${count} PDF ekli` : "PDF ekli"}>
                          <Paperclip size={12} />{count > 1 && <span style={{ marginLeft: 2, fontSize: 10, fontWeight: 700 }}>{count}</span>}
                        </span>
                        {count > 1 && (
                          <span data-testid={`policy-zeyil-${p.id}`} title={`${count - 1} zeyil mevcut`} style={{ marginLeft: 6, fontSize: 9, padding: "2px 5px", borderRadius: 3, background: "#fef3c7", color: "#92400e", fontWeight: 700, letterSpacing: 0.3, verticalAlign: "middle" }}>ZEYİL</span>
                        )}
                      </>
                    );
                  })()}
                </td>
                <td>{p.customer}</td>
                <td><span className="branch-pill">{p.branch}</span></td>
                <td><CompanyName name={p.company} /></td>
                <td>{p.plate ? <span className="plate-chip" data-testid={`policy-plate-${p.id}`}>{p.plate}</span> : <em className="dim">—</em>}</td>
                <td>{money(p.premium)}</td>
                <td className="commission">{money(c.personal)}</td>
                {anyAgent && <td>{ag.agent > 0 ? <span data-testid={`policy-agent-${p.id}`} title={`Müşteri komisyonu %${ag.rate}`}>{money(ag.agent)}</span> : <em className="dim">—</em>}</td>}
                {!compact && (
                  <td className="table-actions">
                    {openPolicyPdf && p.pdfPath && (
                      <button className="row-action" title="PDF'i aç" data-testid={`open-pdf-${p.id}`} onClick={() => openPolicyPdf(p.id)}><ExternalLink size={15} /></button>
                    )}
                    {onEdit && <button className="row-action" title="Düzenle" data-testid={`edit-policy-${p.id}`} onClick={() => onEdit(p)}><Pencil size={15} /></button>}
                    {remove && canDelete && <button className="row-action" title="Sil" data-testid={`delete-policy-${p.id}`} onClick={() => remove("policy", p.id)}><Trash2 size={15} /></button>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!policies.length && <Empty text="Henüz poliçe kaydı yok" />}
    </div>
  );
}

// Inline helper (avoids extra import indirection where PolicyTable needs just the rate for the "any?" check).
function customerCommissionRateInline(policy, customers) {
  const name = (policy?.customer || "").trim().toLocaleLowerCase("tr");
  if (!name) return 0;
  const c = customers.find((x) => (x?.name || "").trim().toLocaleLowerCase("tr") === name);
  const r = Number(c?.commissionRate);
  return Number.isFinite(r) && r > 0 ? r : 0;
}

export function Policies({ policies, customers, branches, settings, query, setQuery, setModal, setPdfImport, setExcelImport, setEditingPolicy, remove, exportCsv, exportExcel, openPolicyPdf, can = () => true }) {
  const MONTHS_FULL = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  const [monthFilter, setMonthFilter] = useState("all"); // "all" | 1..12
  const [yearFilter, setYearFilter] = useState("all"); // "all" | year
  const years = useMemo(() => {
    const set = new Set();
    policies.forEach((p) => { if (p.start) { const y = new Date(p.start).getFullYear(); if (!isNaN(y)) set.add(y); } });
    return Array.from(set).sort((a, b) => b - a);
  }, [policies]);
  const filtered = policies.filter((p) => {
    if (!`${p.customer} ${p.policeNo} ${p.branch} ${p.company} ${p.plate || ""}`.toLocaleLowerCase("tr").includes(query.toLocaleLowerCase("tr"))) return false;
    if (monthFilter !== "all" || yearFilter !== "all") {
      if (!p.start) return false;
      const d = new Date(p.start);
      if (isNaN(d.getTime())) return false;
      if (monthFilter !== "all" && d.getMonth() + 1 !== Number(monthFilter)) return false;
      if (yearFilter !== "all" && d.getFullYear() !== Number(yearFilter)) return false;
    }
    return true;
  });
  const filterActive = monthFilter !== "all" || yearFilter !== "all";
  return (
    <>
      <PageTitle
        eyebrow="PORTFÖY YÖNETİMİ"
        title="Poliçeler"
        description={`${policies.length} poliçe · ${branches.length} branş${filterActive ? ` · Filtre: ${filtered.length} sonuç` : ""}`}
        action="Yeni poliçe"
        onAction={() => { setEditingPolicy(null); setModal("policy"); }}
      />
      <div className="toolbar">
        <div className="inline-search"><Search size={16} /><input data-testid="policy-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Poliçe veya müşteri ara..." /></div>
        <div className="toolbar-actions">
          <select data-testid="policy-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="toolbar-select" style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer" }}>
            <option value="all">Tüm aylar</option>
            {MONTHS_FULL.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select data-testid="policy-year-filter" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="toolbar-select" style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer" }}>
            <option value="all">Tüm yıllar</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {filterActive && <button className="text-button" data-testid="policy-filter-clear" onClick={() => { setMonthFilter("all"); setYearFilter("all"); }} style={{ fontSize: 12 }}>Filtreyi temizle</button>}
          {can("canBulkImport") && <button className="outline-button" data-testid="pdf-import-button" onClick={() => setPdfImport(true)}><FileText size={16} /> PDF'den aktar</button>}
          {can("canBulkImport") && <button className="outline-button" data-testid="excel-import-button" onClick={() => setExcelImport(true)}><Upload size={16} /> Excel'den toplu yükle</button>}
          <button className="outline-button" data-testid="export-excel-button" onClick={exportExcel}><Download size={16} /> Excel yedeği</button>
          <button className="outline-button" data-testid="export-policies-button" onClick={exportCsv}><Download size={16} /> CSV</button>
        </div>
      </div>
      <section className="panel table-panel">
        <PolicyTable
          policies={filtered}
          customers={customers}
          branches={branches}
          settings={settings}
          remove={remove}
          onEdit={(p) => { setEditingPolicy(p); setModal("policy"); }}
          openPolicyPdf={openPolicyPdf}
          canDelete={can("canDeleteRecords")}
        />
      </section>
    </>
  );
}
